import React, { useEffect, useRef, useState } from "react";
import { useLayoutEffect } from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { motion } from "framer-motion";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { toast } from "react-toastify";
import { device } from "../../constants/device-sizes";
import {
  removeMessage,
  removeMessagesFromUserId,
} from "../../store/messages-actions";
import {
  MESSAGE_STATUS_CACHED,
  moveMessage,
} from "../../store/messages-actions";
dayjs.extend(relativeTime);
import { addToBlockList, isUserBlocked } from "../../utils/localstorage";
import BlockConfirmationModal from "./BlockConfirmationModal";


const Text = styled.p`
  color: ${(props) => {
    if (props.color) {
      return props.color;
    } else if (props.status === MESSAGE_STATUS_CACHED) {
      return "var(--clr-cached)";
    } else {
      return "var(--clr-sec)";
    }
  }};
  font-family: ${(props) => {
    if (props.font === 'Caveat') {
      return "'Caveat', cursive";
    } else if (props.font === 'JetBrains Mono') {
      return "'JetBrains Mono', 'Courier New', monospace";
    } else if (props.font === 'Inter') {
      return "'Inter', system-ui, -apple-system, sans-serif";
    } else if (props.font === 'Roboto') {
      return "'Roboto', system-ui, -apple-system, sans-serif";
    } else if (props.font === 'Times New Roman') {
      return "'Times New Roman', Georgia, serif";
    } else if (props.font === 'Inter Bold Small-Caps') {
      return "'Inter', system-ui, -apple-system, sans-serif";
    } else {
      return "inherit";
    }
  }};
  ${(props) => props.font === 'Inter Bold Small-Caps' ? `
    font-variant: small-caps;
    font-weight: bold;
  ` : `
    font-weight: ${props.bold ? "600" : "normal"};
  `}
  text-align: center;
  margin: 0;
  font-size: ${(props) => {
    // Boost Caveat font size by 15% since handwritten fonts appear smaller
    if (props.font === 'Caveat') {
      return Math.round(props.fontSize * 1.15) + 'px';
    } else {
      return props.fontSize + 'px';
    }
  }};
  line-height: ${(props) => 1.2 * props.fontSize + "px"};
  ${(props) => {
    if (props.effect === 'glow') {
      return `
        text-shadow: 
          0 0 5px ${props.color || 'var(--clr-sec)'}, 
          0 0 10px ${props.color || 'var(--clr-sec)'}, 
          0 0 15px ${props.color || 'var(--clr-sec)'};
        animation: glow-pulse 2s ease-in-out infinite alternate;
      `;
    }
    return '';
  }}

  @keyframes glow-pulse {
    from {
      text-shadow: 
        0 0 5px ${(props) => props.color || 'var(--clr-sec)'}, 
        0 0 10px ${(props) => props.color || 'var(--clr-sec)'}, 
        0 0 15px ${(props) => props.color || 'var(--clr-sec)'};
    }
    to {
      text-shadow: 
        0 0 10px ${(props) => props.color || 'var(--clr-sec)'}, 
        0 0 20px ${(props) => props.color || 'var(--clr-sec)'}, 
        0 0 30px ${(props) => props.color || 'var(--clr-sec)'};
    }
  }
`;

const DateText = styled.p`
  color: ${(props) => {
    if (props.color) {
      return props.color;
    } else if (props.status === MESSAGE_STATUS_CACHED) {
      return "var(--clr-cached)";
    } else {
      return "var(--clr-sec)";
    }
  }};
  text-align: center;
  font-size: var(--fs-body-small);
  margin: 0;
  padding: 0;
`;

const NicknameText = styled.p`
  color: ${(props) => {
    if (props.color) {
      return props.color;
    } else if (props.status === MESSAGE_STATUS_CACHED) {
      return "var(--clr-cached)";
    } else {
      return "var(--clr-sec)";
    }
  }};
  font-family: ${(props) => {
    if (props.font === 'Caveat') {
      return "'Caveat', cursive";
    } else if (props.font === 'JetBrains Mono') {
      return "'JetBrains Mono', 'Courier New', monospace";
    } else if (props.font === 'Inter') {
      return "'Inter', system-ui, -apple-system, sans-serif";
    } else if (props.font === 'Roboto') {
      return "'Roboto', system-ui, -apple-system, sans-serif";
    } else if (props.font === 'Times New Roman') {
      return "'Times New Roman', Georgia, serif";
    } else if (props.font === 'Inter Bold Small-Caps') {
      return "'Inter', system-ui, -apple-system, sans-serif";
    } else {
      return "inherit";
    }
  }};
  ${(props) => props.font === 'Inter Bold Small-Caps' ? `
    font-variant: small-caps;
    font-weight: bold;
  ` : `
    font-weight: 300;
  `}
  text-align: center;
  font-size: var(--fs-body-small);
  margin: 0.25rem 0 0 0;
  padding: 0;
  opacity: 0.7;
  font-style: italic;
`;

const Container = styled.div`
  position: absolute;
  text-align: center;
  word-wrap: break-word;
  overflow-wrap: break-word;
  overflow: hidden;
  max-width: 45%;
  @media ${device.tablet} {
    max-width: 25%;
  }
  opacity: 0;
  will-change: transform, opacity;
  margin: 0;
  padding: 0.5rem;
  -webkit-user-select: none; /* Safari */
  -moz-user-select: none; /* Firefox */
  -ms-user-select: none; /* IE10+/Edge */
  user-select: none; /* Standard */
`;

const BlockButton = styled.button`
  background-color: var(--clr-tert);
  color: var(--clr-sec);
  border: none;
  margin: 0;
  padding: 0.5rem 1rem;
`;

const BanButton = styled.button`
  background-color: #ff4444;
  color: white;
  border: none;
  margin: 0;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-weight: 600;
  
  &:hover {
    background-color: #dd3333;
  }
`;

const FriendRequestButton = styled.button`
  background-color: var(--clr-green);
  color: white;
  border: none;
  margin: 0;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-weight: 600;
  
  &:hover {
    background-color: #28a745;
  }
`;

const ReportButton = styled.button`
  background-color: #ff6b35;
  color: white;
  border: none;
  margin: 0;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-weight: 600;
  
  &:hover {
    background-color: #e55a2b;
  }
`;

const ViewProfileButton = styled.button`
  background-color: #6c757d;
  color: white;
  border: none;
  margin: 0;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-weight: 600;
  
  &:hover {
    background-color: #5a6268;
  }
`;

const CopyTextButton = styled.button`
  background-color: #17a2b8;
  color: white;
  border: none;
  margin: 0;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-weight: 600;
  
  &:hover {
    background-color: #138496;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: center;
`;

const Message = (props) => {
  const dispatch = useDispatch();
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [messageMoved, setMessageMoved] = useState(false);
  const [blockButtonVisible, setBlockButtonVisible] = useState(false);
  const [blockModalOpen, setBlockModalOpen] = useState(false);

  const messageRef = useRef();

  function onBlockClicked() {
    setBlockModalOpen(true);
  }

  function handleBlockConfirm() {
    let blockedUserId = props.message.userId;

    // Add their userId to the block list with nickname and message text
    addToBlockList(blockedUserId, props.message.nickname, props.message.messageText);

    // Remove all of their messages
    dispatch(removeMessagesFromUserId(blockedUserId));

    // Make block API request
    props.sendBlockedUserToServer(props.message);
    
    setBlockModalOpen(false);
  }

  function handleBlockModalClose() {
    setBlockModalOpen(false);
  }

  function onBanClicked() {
    if (props.onModBanClick) {
      props.onModBanClick(props.message);
    }
  }

  function onFriendRequestClicked() {
    if (props.onFriendRequestClick) {
      props.onFriendRequestClick(props.message.userId);
    }
  }

  function onReportClicked() {
    if (props.onReportClick) {
      props.onReportClick(props.message);
    }
  }

  function onViewProfileClicked() {
    if (props.onViewProfileClick) {
      props.onViewProfileClick(props.message.userId);
    }
  }

  function onCopyTextClicked() {
    if (messageText) {
      navigator.clipboard.writeText(messageText);
      toast.success('Message copied to clipboard');
    }
  }

  const { width, height } = size;
  const {
    id,
    rect,
    fontSize,
    fadeOutDuration,
    messageText,
    status,
    color,
    font,
    effect,
    bold,
    createdAt,
    nickname,
    age,
    pronouns,
  } = props.message;

  useLayoutEffect(() => {
    if (messageRef) {
      setSize({
        width: messageRef.current.clientWidth,
        height: messageRef.current.clientHeight,
      });
    }
  }, [messageRef]);

  useEffect(() => {
    if (height === 0 || width === 0 || messageMoved) {
      return;
    }
    setMessageMoved(true);
    dispatch(moveMessage(id, width, height));
  }, [height, width, dispatch, id, messageMoved]);

  // Animation handled by framer-motion now

  const [isHovered, setIsHovered] = useState(false);
  const [animationComplete, setAnimationComplete] = useState(false);
  const [shouldFadeOut, setShouldFadeOut] = useState(false);
  const fadeOutTimerRef = useRef(null);

  function focusMessage() {
    setIsHovered(true);
    // Cancel any pending removal and restore opacity
    if (fadeOutTimerRef.current) {
      clearTimeout(fadeOutTimerRef.current);
      fadeOutTimerRef.current = null;
    }
    setShouldFadeOut(false);
  }

  function unFocusMessage() {
    setIsHovered(false);
    // If animation is complete, trigger fade out
    if (animationComplete && fadeOutDuration) {
      setShouldFadeOut(true);
      // Remove after fade-out animation
      // Match the normal fade duration (last 30% of the total animation)
      const fadeOutTime = (fadeOutDuration + 1000) * 0.3;
      fadeOutTimerRef.current = setTimeout(() => {
        dispatch(removeMessage(id));
      }, fadeOutTime);
    }
  }

  // Handle case where animation completes while not hovered
  useEffect(() => {
    if (animationComplete && !isHovered && fadeOutDuration && !shouldFadeOut) {
      dispatch(removeMessage(id));
    }
  }, [animationComplete, isHovered, fadeOutDuration, shouldFadeOut, dispatch, id]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (fadeOutTimerRef.current) {
        clearTimeout(fadeOutTimerRef.current);
      }
    };
  }, []);

  // console.log("Message with tooltipdisabled", props.message);

  function onClickContainer() {
    if (props.message.userId !== "self") {
      setBlockButtonVisible(!blockButtonVisible);
    }
  }

  return (
    <Container
      as={motion.div}
      ref={messageRef}
      style={{
        top: rect.top,
        left: rect.left,
        fontSize: fontSize,
      }}
      initial={{ opacity: 0 }}
      animate={{
        opacity: fadeOutDuration 
          ? shouldFadeOut
            ? 0  // Fade to 0 when unhovered after animation complete
            : isHovered 
            ? 1  // Keep full opacity when hovered
            : [0, 1, 1, 0.15]  // Normal fade animation
          : isHovered 
          ? 1 
          : 0.85
      }}
      transition={{
        duration: shouldFadeOut 
          ? (fadeOutDuration + 1000) * 0.3 / 1000  // Match normal fade-out duration
          : fadeOutDuration 
          ? (fadeOutDuration + 1000) / 1000 
          : 0.75,
        times: fadeOutDuration && !shouldFadeOut ? [0, 1000 / (fadeOutDuration + 1000), 0.7, 1] : undefined,
        ease: shouldFadeOut ? [0.9, 0.8, 0.75, 1] : [1, 0.75, 0.9, 0.8]
      }}
      onAnimationComplete={() => {
        if (fadeOutDuration) {
          setAnimationComplete(true);
        }
      }}
      onMouseOver={focusMessage}
      onMouseOut={unFocusMessage}
      onClick={onClickContainer}
    >
      <Text fontSize={fontSize} status={status} color={color} font={font} effect={effect} bold={bold}>
        {messageText}
      </Text>
      {(nickname || age || pronouns) && (
        <NicknameText fontSize={fontSize} status={status} color={color} font={font}>
          - {[
              nickname || 'Anon',
              age,
              pronouns
            ].filter(Boolean).join(' · ')}
        </NicknameText>
      )}
      {status === MESSAGE_STATUS_CACHED && (
        <DateText>{dayjs(createdAt).fromNow()}</DateText>
      )}
      {blockButtonVisible && (
        <ButtonContainer>
          <CopyTextButton onClick={onCopyTextClicked}>
            Copy text
          </CopyTextButton>
          {props.message.userId !== "self" && (
            <>
              <ViewProfileButton onClick={onViewProfileClicked}>
                View profile
              </ViewProfileButton>
              <FriendRequestButton onClick={onFriendRequestClicked}>
                Send friend request
              </FriendRequestButton>
              <ReportButton onClick={onReportClicked}>
                Report message
              </ReportButton>
              {!isUserBlocked(props.message.userId) && (
                <BlockButton onClick={onBlockClicked}>Block this user</BlockButton>
              )}
              {props.isMod && (
                <BanButton onClick={onBanClicked}>Ban user</BanButton>
              )}
            </>
          )}
        </ButtonContainer>
      )}
      <BlockConfirmationModal
        isOpen={blockModalOpen}
        onClose={handleBlockModalClose}
        onConfirm={handleBlockConfirm}
        message={props.message}
      />
    </Container>
  );
};

function messagePropsAreEqual(prevMessage, nextMessage) {
  return prevMessage.message === nextMessage.message;
}

export default React.memo(Message, messagePropsAreEqual);
